import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Address3HelperService, AddressFormGroup } from '../address3-helper.service';
import { CustomValidators, regexValidators } from '@shared/customValidators';
import { first } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NGXLogger } from 'ngx-logger';
import { Address } from '../../../models/data-model';

@Component({
    selector: 'address3-default',
    templateUrl: './address3-default.component.html'
})
export class Address3DefaultComponent implements OnInit {

    @Input()
    readonly = false;

    @Input()
    addressFormGroup: FormGroup<AddressFormGroup>;

    constructor(
        private readonly logger: NGXLogger,
        private readonly destroyRef: DestroyRef,
        private readonly address3Helper: Address3HelperService) {
    }

    get selectedCountry(): string {
        return this.addressFormGroup?.controls?.countryISO?.value;
    }

    get showingSearchResult(): boolean {
        return Boolean(this.addressFormGroup.controls.searchRefCode?.value);
    }

    ngOnInit(): void {
        this.address3Helper.resetValidators(this.addressFormGroup);
        this.initValidators();
        this.address3Helper.updateValidity(this.addressFormGroup);

        if(this.showingSearchResult) {
            this.invalidateSearchResultOnFirstEdit();
        }
    }

    private initValidators() {

        // line 1
        this.addressFormGroup.controls.line1.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("line1"))
        ]);

        // line 2
        this.addressFormGroup.controls.line2.setValidators([
            Validators.maxLength(CustomValidators.getAddressMaxlength("line2"))
        ]);

        this.addressFormGroup.controls.city.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("city"))
        ]);

        this.addressFormGroup.controls.province.setValidators([
            Validators.maxLength(CustomValidators.getAddressMaxlength("province"))
        ]);

        this.initPostcodeValidator();
        this.addressFormGroup.controls.countryISO.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(() => {
            this.initPostcodeValidator();
        });

    }

    initPostcodeValidator() {
        this.addressFormGroup.controls.postalCode.setValidators([
            Validators.required,
            Validators.maxLength(CustomValidators.getAddressMaxlength("postalCode"))
        ]);
        if(regexValidators.postcodesByCountry[this.selectedCountry]) {
            this.addressFormGroup.controls.postalCode.addValidators(Validators.pattern(regexValidators.postcodesByCountry[this.selectedCountry]));
        }
    }

    mapSearchResult(place: google.maps.places.PlaceResult) {
        const route = this.address3Helper.getGooglePlaceComponent(place, "route");
        const streetNumber = this.address3Helper.getGooglePlaceComponent(place, "street_number");
        const locality = this.address3Helper.getGooglePlaceComponent(place, "locality");
        const postalCode = this.address3Helper.getGooglePlaceComponent(place, "postal_code");
        const level2 = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_2");
        const level1 = this.address3Helper.getGooglePlaceComponent(place, "administrative_area_level_1");

        let address: Partial<Address> = {};

        switch (this.selectedCountry) {
            case "ESP":
                address = {
                    line1: [route, streetNumber].filter(Boolean).join(", "),
                    line2: "",
                    city: locality,
                    province: "",
                    postalCode,
                };
                break;
            case "FRA":
                address = {
                    line1: [streetNumber, route].filter(Boolean).join(" "),
                    line2: "",
                    city: locality,
                    province: "",
                    postalCode,
                };
                break;
            case "NLD":
            case "DEU":
                address = {
                    line1: [route, streetNumber].filter(Boolean).join(" "),
                    line2: "",
                    city: locality,
                    province: "",
                    postalCode,
                };
                break;
            default:
                address = {
                    line1: route
                        ? [route, streetNumber].filter(Boolean).join(" ")
                        : (place.formatted_address?.split(",")[0].trim() ?? ""),
                    line2: "",
                    city: locality || level2,
                    province: level1,
                    postalCode
                };
        }

        address.searchRefCode = place.place_id;

        this.addressFormGroup.patchValue(address);
        this.invalidateSearchResultOnFirstEdit();
    }

    invalidateSearchResultOnFirstEdit() {
        this.logger.debug("Manually edited, invalidating search results");
        this.addressFormGroup.valueChanges.pipe(
            takeUntilDestroyed(this.destroyRef),
            first()
        ).subscribe(() => {
            this.addressFormGroup.controls.searchRefCode.setValue("");
        });
    }

}
